import Button from 'react-bootstrap/Button';
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./AsphaltEmission.css";
import config from "../../config";
import stateContext from '../../data/state_context';
import AsphaltEmissionType from "./AsphaltEmissionType";
import axios from "axios";
import { Col, Container, Form, Modal, Row, ToggleButton, ButtonGroup } from 'react-bootstrap';
import SideNav from '../SideNav/SideNav';
import HelpIcon from '../HelpIcon/HelpIcon';
import { useStateContext } from '../../context/StateContext';
import useFormValidation from '../../hooks/useFormValidation';
import ValidationError from '../ValidationError/ValidationError';

const AsphaltEmission = () => {
    let { id } = useParams();
    const { selectedState, setSelectedState, isStateSelected } = useStateContext();
    const { handleBlur, getFieldState, validateAll } = useFormValidation();
    const [data, setData] = useState(AsphaltEmissionType);
    const [unitSystem, setUnitSystem] = useState('metric'); // 'metric' or 'imperial'
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const backref = queryParams.get('backref');

    const [error, setError] = useState(null);
    const [errorPopupShow, setErrorPopupShow] = useState(false);
    const [stateWarningShow, setStateWarningShow] = useState(false);
    const [validationErrorShow, setValidationErrorShow] = useState(false);
    const [emissionsUnit, setEmissionsUnit] = useState('kgCO2eq_m3'); // 'kgCO2eq_m3' or 'kgCO2eq_ton'

    let navigate = useNavigate();

    const conversionFactors = {
        kgm3_to_lbyd3: 1.68555, // kg/m³ → lb/yd³
        kgCO2_per_t_to_lbCO2_per_ton: 2.20462 / 1.10231,
        kg_to_short_ton: 0.00110231, // kg to short tons
        kgm3_to_kgton: 0.7646 // kg/m³ to kg/short-ton (assuming density of 1.68555 kg/m³ = 1 lb/yd³)
    };

    const toggleUnitSystem = () => {
        setUnitSystem(prev => prev === 'metric' ? 'imperial' : 'metric');
    };

    const toggleEmissionsUnit = () => {
        setEmissionsUnit(prev => prev === 'kgCO2eq_m3' ? 'kgCO2eq_ton' : 'kgCO2eq_m3');
    };

    const displayValue = (value, unitType) => {
        if (unitType !== 'emissions' && unitType !== 'emission intensity') {
            // handle non-emissions units as before
            if (unitSystem === 'metric') {
                return value;
            }

            switch(unitType) {
                case 'cost':
                    return value * conversionFactors.kgm3_to_lbyd3;
                case 'percentage':
                    return value;
                default:
                    return value;
            }
        }

        // handle emissions units specifically
        if (unitType === 'emission intensity') {
            // for bitumen impact (already in kg/t or lb/ton)
            if (unitSystem === 'metric') {
                return value; // keep as kg/t
            } else {
                return value * conversionFactors.kgCO2_per_t_to_lbCO2_per_ton; // convert to lb/ton
            }
        } else {
            // for other emissions (kg/m³ or lb/yd³ or kg/ton)
            if (unitSystem === 'metric') {
                // metric system - show either kg/m³ or kg/ton
                return emissionsUnit === 'kgCO2eq_m3'
                    ? value
                    : value * conversionFactors.kgm3_to_kgton;
            } else {
                // imperial system - show either lb/yd³ or kg/ton
                if (emissionsUnit === 'kgCO2eq_ton') {
                    // show kg/ton - convert from kg/m³ to kg/ton
                    return value * conversionFactors.kgm3_to_kgton;
                } else {
                    // Show lb/yd³ - convert from kg/m³ to lb/yd³
                    return value * conversionFactors.kgm3_to_lbyd3;
                }
            }
        }
    };

    // Convert from display units back to storage units (metric)
    const inverseDisplayValue = (value, unitType) => {
        if (unitSystem === 'metric') {
            return value;
        }
        switch(unitType) {
            case 'cost':
                return value / conversionFactors.kgm3_to_lbyd3;
            case 'emission intensity':
                return value / conversionFactors.kgCO2_per_t_to_lbCO2_per_ton;
            case 'emissions':
                if (emissionsUnit === 'kgCO2eq_ton') {
                    return value / conversionFactors.kgm3_to_kgton;
                } else {
                    return value / conversionFactors.kgm3_to_lbyd3;
                }
            case 'percentage':
            default:
                return value;
        }
    };

    const displayUnit = (metricUnit, imperialUnit) => {
        return unitSystem === 'metric' ? metricUnit : imperialUnit;
    };

    const displayEmissionsUnit = (metricBase, imperialBase) => {
        if (emissionsUnit === 'kgCO2eq_ton') {
            return <>kg CO<sub>2</sub> eq/short ton</>;
        }
        return unitSystem === 'metric' ? metricBase : imperialBase;
    };

    useEffect(() => {
        axios.get(`${config.apiUrl}/api/emission/asphalt/${id}`)
            .then(response => {
                const responseData = response.data;

                responseData.material.total = parseFloat(responseData.material.bitumenContent * responseData.material.bitumenImpact * 2.2) +
                parseFloat(responseData.material.gravelImpact) + parseFloat(responseData.material.sandImpact) + parseFloat(responseData.material.rapContent) + parseFloat(responseData.material.wapContent);
                responseData.energy.total = parseFloat(responseData.energy.asphaltHeatingImpact);
                responseData.transportation.total = parseFloat(responseData.transportation.transportationGHG);
                responseData.total = parseFloat(responseData.material.total) + parseFloat(responseData.energy.total) + parseFloat(responseData.transportation.total);

            setData(responseData);
                setSelectedState(response.data.state);
            })
            .catch(error => {
                //setError(error.message);
            });
    }, [id]);

    const handleStateChange = (event) => {
        const state = event.target.value;
        setSelectedState(state);

        axios.get(`${config.apiUrl}/api/emission/asphalt/state/${state}`).then(response => {
            const responseData = response.data;

            responseData.material.total = parseFloat(responseData.material.bitumenContent * responseData.material.bitumenImpact * 2.2) +
            parseFloat(responseData.material.gravelImpact) + parseFloat(responseData.material.sandImpact) + parseFloat(responseData.material.rapContent) + parseFloat(responseData.material.wapContent);
            responseData.energy.total = parseFloat(responseData.energy.asphaltHeatingImpact);
            responseData.transportation.total = parseFloat(responseData.transportation.transportationGHG);
            responseData.total = parseFloat(responseData.material.total) + parseFloat(responseData.energy.total) + parseFloat(responseData.transportation.total);

            setData(responseData);
        }).catch(error => {
            console.log(error);
        });
    };

    function handleChange(e) {
        const { name, value, type, checked } = e.target;
        const unitType = e.target.dataset.unitType; // Get unit type from data attribute
        const nameParts = name.split('.');  // Split the name by dots to handle nested keys, e.g., 'user.name'

        // Convert from display units to storage units (metric) if needed
        let storageValue = value;
        if (unitType && type === 'number' && value !== '') {
            storageValue = inverseDisplayValue(parseFloat(value), unitType);
        }

        setData(prevFormData => {
            // Function to recursively update nested objects
            const updateNestedObject = (obj, parts, val) => {
                const part = parts.shift();  // Get the next property name in the path
                if (parts.length > 0) {
                    obj[part] = updateNestedObject(obj[part] || {}, parts, val);
                } else {
                    obj[part] = type === 'checkbox' ? checked : val;  // Set the value for the last part of the path
                }
                return { ...obj };
            };

            const updatedFormData = updateNestedObject({ ...prevFormData }, nameParts, storageValue);

            updatedFormData.material.total = parseFloat(updatedFormData.material.bitumenContent * updatedFormData.material.bitumenImpact * 2.2) +
            parseFloat(updatedFormData.material.gravelImpact) + parseFloat(updatedFormData.material.sandImpact) + parseFloat(updatedFormData.material.rapContent) + parseFloat(updatedFormData.material.wapContent);
            updatedFormData.energy.total = parseFloat(updatedFormData.energy.asphaltHeatingImpact);
            updatedFormData.transportation.total = parseFloat(updatedFormData.transportation.transportationGHG);
            updatedFormData.total = parseFloat(updatedFormData.material.total) + parseFloat(updatedFormData.energy.total) + parseFloat(updatedFormData.transportation.total);

            return updatedFormData
        });
    }

    const validateForm = () => {
        const fields = [
            { name: 'material.bitumenContent', value: data.material.bitumenContent, type: 'numberGteZero' },
            { name: 'material.bitumenImpact', value: data.material.bitumenImpact, type: 'numberGteZero' },
            { name: 'material.gravelImpact', value: data.material.gravelImpact, type: 'numberGteZero' },
            { name: 'material.sandImpact', value: data.material.sandImpact, type: 'numberGteZero' },
            { name: 'material.rapContent', value: data.material.rapContent, type: 'numberGteZero' },
            { name: 'material.wapContent', value: data.material.wapContent, type: 'numberGteZero' },
            { name: 'transportation.transportationGHG', value: data.transportation.transportationGHG, type: 'number' },
            { name: 'energy.asphaltHeatingImpact', value: data.energy.asphaltHeatingImpact, type: 'number' },
        ];
        return validateAll(fields);
    };

    const handleSubmit = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.put(`${config.apiUrl}/api/emission/asphalt/${id}`, data).then(response => {
            navigate(`/pavement-design/${id}`);

        }).catch(error => {
        });
    }

    const goBack = () => {
        if (backref) {
            navigate(`/${backref}/${id}`);
        } else {
            navigate(`/pavement-design/${id}`);
        }
    };

    const goToBasicContext = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.put(`${config.apiUrl}/api/emission/asphalt/${id}`, data).then(response => {
            navigate(`/pavement-context-basic/${id}`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const goToDetailedContext = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.put(`${config.apiUrl}/api/emission/asphalt/${id}`, data).then(response => {
            navigate(`/pavement-context-custom/${id}`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const goToPavementDesign = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.put(`${config.apiUrl}/api/emission/asphalt/${id}`, data).then(response => {
            navigate(`/pavement-design/${id}`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const goToPedegreeMatrix = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.put(`${config.apiUrl}/api/emission/asphalt/${id}`, data).then(response => {
            navigate(`/pedegree-matrix/${id}?backref=asphalt-embodied-emission`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const goToConcreteEmbodiedEmission = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.put(`${config.apiUrl}/api/emission/asphalt/${id}`, data).then(response => {
            navigate(`/concrete-embodied-emission/${id}?backref=asphalt-embodied-emission`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }
    const goToResults = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.put(`${config.apiUrl}/api/emission/asphalt/${id}`, data).then(response => {
            navigate(`/concrete-embodied-emission/${id}?backref=asphalt-embodied-emission`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const handleErrorPopupClose = () => setErrorPopupShow(false);
    const handleErrorPopupShow = () => setErrorPopupShow(true);

    // Handle input blur - convert to number and trigger validation
    const handleInputBlur = (e) => {
        const numericValue = e.target.value === '' ? 0 : parseFloat(e.target.value);
        const validationType = e.target.dataset.validationType || 'number';
        handleChange({ target: { name: e.target.name, value: numericValue, type: 'number', dataset: e.target.dataset } });
        handleBlur(e.target.name, numericValue, validationType);
    };

    return (
        <>
            <SideNav active="asphalt-embodied-emission" id={id} passed={["pavement-context-basic", "pavement-context-custom", "concrete-embodied-emission"]}
                goToBasicContext={goToBasicContext}
                goToDetailedContext={goToDetailedContext}
                goToAsphaltEmbodiedEmission={() => { }}
                goToConcreteEmbodiedEmission={goToConcreteEmbodiedEmission}
                goToPedegreeMatrix={goToPedegreeMatrix}
                goToPavementDesign={goToPavementDesign}
                goToResults={goToResults}
                isStateSelected={isStateSelected}
                onNavigationBlocked={() => setStateWarningShow(true)}
            />
            <Container>
            <Row>
                <Col>
                    <div className="pcb__sub">
                        <div className="pcb__box cee__toppanel">
                            {/*  */}
                            <div className="cee__el-row">
                                <div className="cee__label-top">State</div>
                                <Form.Select
                                    size="sm"
                                    style={{ width: "250px" }} value={selectedState} onChange={handleStateChange}>
                                    {!isStateSelected && <option value="">Select a State</option>}
                                    {Object.keys(stateContext).map(state => (
                                        <option key={state} value={state}>{state}</option>
                                    ))}
                                </Form.Select>
                            </div>

                            {/* unit toggle button */}
                            <div className="cee__el-row">
                                <div className="cee__label-top">Unit Toggle</div>
                                <ButtonGroup size="sm">
                                    <ToggleButton
                                        id="unit-toggle"
                                        type="checkbox"
                                        variant={unitSystem === 'metric' ? 'primary' : 'outline-primary'}
                                        checked={unitSystem === 'metric'}
                                        onChange={toggleUnitSystem}
                                    >
                                        Metric
                                    </ToggleButton>
                                    <ToggleButton
                                        id="unit-toggle"
                                        type="checkbox"
                                        variant={unitSystem === 'imperial' ? 'primary' : 'outline-primary'}
                                        checked={unitSystem === 'imperial'}
                                        onChange={toggleUnitSystem}
                                    >
                                        Imperial
                                    </ToggleButton>
                                </ButtonGroup>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="pcb__sub">
                        <h3 className="pcb__sub-title">
                            Materials (A1)
                            <HelpIcon text="Raw material extraction and processing. Includes mining, quarrying, and material production emissions." />
                        </h3>
                        <div className="pcb__box">
                            <table style={{ width: "90%" }}>
                                <tr>
                                    <td className="pcc__table-label">Bitumen content</td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            step="0.01"
                                            style={{ width: "95%" }}
                                            name="material.bitumenContent"
                                            id="material.bitumenContent"
                                            data-unit-type="percentage"
                                            data-validation-type="numberGteZero"
                                            value={data.material.bitumenContent === '' ? '' : parseFloat(displayValue(Number(data.material.bitumenContent ?? 0), 'percentage').toFixed(2))}
                                            disabled={!isStateSelected}
                                            onChange={handleChange}
                                            onBlur={handleInputBlur}
                                            isInvalid={getFieldState('material.bitumenContent').isInvalid} />
                                        <ValidationError message={getFieldState('material.bitumenContent').errorMessage} />
                                    </td>
                                    <td className="cee_value"></td>
                                </tr>

                                <tr>
                                    <td className="pcc__table-label">Bitumen impact</td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            step="0.01"
                                            style={{ width: "95%" }}
                                            name="material.bitumenImpact"
                                            id="material.bitumenImpact"
                                            data-unit-type="emission intensity"
                                            data-validation-type="numberGteZero"
                                            value={data.material.bitumenImpact === '' ? '' : parseFloat(displayValue(Number(data.material.bitumenImpact ?? 0), 'emission intensity').toFixed(2))}
                                            disabled={!isStateSelected}
                                            onChange={handleChange}
                                            onBlur={handleInputBlur}
                                            isInvalid={getFieldState('material.bitumenImpact').isInvalid} />
                                        <ValidationError message={getFieldState('material.bitumenImpact').errorMessage} />
                                    </td>
                                    <td className="cee_value">
                                        {displayUnit(<>kg CO<sub>2</sub>/t<sub>asphalt</sub></>,<>lb CO<sub>2</sub>/short ton<sub>asphalt</sub></>)}
                                    </td>
                                </tr>

                                <tr>
                                    <td className="pcc__table-label">Gravel impact</td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            step="0.01"
                                            style={{ width: "95%" }}
                                            name="material.gravelImpact"
                                            id="material.gravelImpact"
                                            data-unit-type="emissions"
                                            data-validation-type="numberGteZero"
                                            value={data.material.gravelImpact === '' ? '' : parseFloat(displayValue(Number(data.material.gravelImpact ?? 0), 'emissions').toFixed(2))}
                                            disabled={!isStateSelected}
                                            onChange={handleChange}
                                            onBlur={handleInputBlur}
                                            isInvalid={getFieldState('material.gravelImpact').isInvalid} />
                                        <ValidationError message={getFieldState('material.gravelImpact').errorMessage} />
                                    </td>
                                    <td className="cee_value">
                                    {displayEmissionsUnit(
                                        <>kg CO<sub>2</sub>/m<sup>3</sup><sub>asphalt</sub></>,
                                        <>lb CO<sub>2</sub>/yd<sup>3</sup><sub>asphalt</sub></>
                                    )}
                                    </td>
                                    </tr>

                                    <tr>
                                    <td className="pcc__table-label">Sand impact</td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            step="0.01"
                                            style={{ width: "95%" }}
                                            name="material.sandImpact"
                                            id="material.sandImpact"
                                            data-unit-type="emissions"
                                            data-validation-type="numberGteZero"
                                            value={data.material.sandImpact === '' ? '' : parseFloat(displayValue(Number(data.material.sandImpact ?? 0), 'emissions').toFixed(2))}
                                            disabled={!isStateSelected}
                                            onChange={handleChange}
                                            onBlur={handleInputBlur}
                                            isInvalid={getFieldState('material.sandImpact').isInvalid} />
                                        <ValidationError message={getFieldState('material.sandImpact').errorMessage} />
                                    </td>
                                    <td className="cee_value">
                                        {displayEmissionsUnit(
                                        <>kg CO<sub>2</sub>/m<sup>3</sup><sub>asphalt</sub></>,
                                        <>lb CO<sub>2</sub>/yd<sup>3</sup><sub>asphalt</sub></>
                                        )}
                                    </td>
                                    </tr>

                                <tr>
                                    <td colSpan={3} className="aee__column--top">RAP and WAP Modification</td>
                                </tr>

                                <tr>
                                    <td className="pcc__table-label">RAP Content</td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            step="0.01"
                                            style={{ width: "95%" }}
                                            name="material.rapContent"
                                            id="material.rapContent"
                                            data-unit-type="percentage"
                                            data-validation-type="numberGteZero"
                                            value={data.material.rapContent === '' ? '' : parseFloat(displayValue(Number(data.material.rapContent ?? 0), 'percentage').toFixed(2))}
                                            disabled={!isStateSelected}
                                            onChange={handleChange}
                                            onBlur={handleInputBlur}
                                            isInvalid={getFieldState('material.rapContent').isInvalid} />
                                        <ValidationError message={getFieldState('material.rapContent').errorMessage} />
                                    </td>
                                    <td className="cee_value"></td>
                                </tr>

                                <tr>
                                    <td className="pcc__table-label">WAP Content</td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            step="0.01"
                                            style={{ width: "95%" }}
                                            name="material.wapContent"
                                            id="material.wapContent"
                                            data-unit-type="percentage"
                                            data-validation-type="numberGteZero"
                                            value={data.material.wapContent === '' ? '' : parseFloat(displayValue(Number(data.material.wapContent ?? 0), 'percentage').toFixed(2))}
                                            disabled={!isStateSelected}
                                            onChange={handleChange}
                                            onBlur={handleInputBlur}
                                            isInvalid={getFieldState('material.wapContent').isInvalid} />
                                        <ValidationError message={getFieldState('material.wapContent').errorMessage} />
                                    </td>
                                    <td className="cee_value"></td>
                                </tr>
                            </table>
                        </div>
                    </div>

                </Col>
                <Col>
                    <div className="pcb__sub">
                        <h3 className="pcb__sub-title">
                            Transportation (A2)
                            <HelpIcon text="Transport of materials to the manufacturing plant. Based on distance and transport mode." />
                        </h3>
                        <div className="pcb__box">
                            <table style={{ width: "90%" }}>
                                <tr>
                                    <td className="pcc__table-label">Transportation GHG</td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            step="0.01"
                                            style={{ width: "95%" }}
                                            name="transportation.transportationGHG"
                                            id="transportation.transportationGHG"
                                            data-unit-type="emissions"
                                            value={data.transportation.transportationGHG === '' ? '' : parseFloat(displayValue(Number(data.transportation.transportationGHG ?? 0), 'emissions').toFixed(2))}
                                            disabled={!isStateSelected}
                                            onChange={handleChange}
                                            onBlur={handleInputBlur}
                                            isInvalid={getFieldState('transportation.transportationGHG').isInvalid} />
                                        <ValidationError message={getFieldState('transportation.transportationGHG').errorMessage} />
                                    </td>
                                    <td className="cee_value">
                                        {displayEmissionsUnit(
                                        <>kg CO<sub>2</sub>/m<sup>3</sup><sub>asphalt</sub></>,
                                        <>lb CO<sub>2</sub>/yd<sup>3</sup><sub>asphalt</sub></>
                                        )}
                                    </td>
                                </tr>

                            </table>
                        </div>
                    </div>
                    <div className="pcb__sub">
                        <h3 className="pcb__sub-title">
                            Energy (A3)
                            <HelpIcon text="Energy consumed during manufacturing. Includes electricity, natural gas, and fuel usage at the plant." />
                        </h3>
                        <div className="pcb__box">
                            <table style={{ width: "90%" }}>
                                <tr>
                                    <td className="pcc__table-label">Asphalt heating impact</td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            step="0.01"
                                            style={{ width: "95%" }}
                                            name="energy.asphaltHeatingImpact"
                                            id="energy.asphaltHeatingImpact"
                                            data-unit-type="emissions"
                                            value={data.energy.asphaltHeatingImpact === '' ? '' : parseFloat(displayValue(Number(data.energy.asphaltHeatingImpact ?? 0), 'emissions').toFixed(2))}
                                            disabled={!isStateSelected}
                                            onChange={handleChange}
                                            onBlur={handleInputBlur}
                                            isInvalid={getFieldState('energy.asphaltHeatingImpact').isInvalid} />
                                        <ValidationError message={getFieldState('energy.asphaltHeatingImpact').errorMessage} />
                                    </td>
                                    <td className="cee_value">
                                        {displayEmissionsUnit(
                                            <>kg CO<sub>2</sub>/m<sup>3</sup><sub>asphalt</sub></>,
                                            <>lb CO<sub>2</sub>/yd<sup>3</sup><sub>asphalt</sub></>
                                        )}
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>

                    <div className="pcb__sub">
                        <h3 className="pcb__sub-title">Total Emissions (A1+A2+A3) and Total Cost</h3>
                        <div className="pcb__box">

                            <table style={{ width: "90%" }}>
                                <tr>
                                    <th>Material (A1)</th>
                                    <th>Transportation (A2)</th>
                                    <th>Energy (A3)</th>
                                </tr>
                                <tr>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            step="0.0001"
                                            style={{ width: "95%" }}
                                            name="material.total"
                                            id="material.total"
                                            value={parseFloat(displayValue(Number(data.material.total ?? 0), 'emissions').toFixed(2))}
                                            disabled={!isStateSelected}
                                    onChange={handleChange} />
                                    </td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            step="0.0001"
                                            style={{ width: "95%" }}
                                            name="transportation.total"
                                            id="transportation.total"
                                            value={parseFloat(displayValue(Number(data.transportation.total ?? 0), 'emissions').toFixed(2))}
                                            disabled={!isStateSelected}
                                    onChange={handleChange} />
                                    </td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            step="0.0001"
                                            style={{ width: "95%" }}
                                            name="energy.total"
                                            id="energy.total"
                                            value={parseFloat(displayValue(Number(data.energy.total ?? 0), 'emissions').toFixed(2))}
                                            disabled={!isStateSelected}
                                    onChange={handleChange} />
                                    </td>
                                </tr>
                                <tr>
                                    <td>Total Emissions</td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            step="0.0001"
                                            style={{ width: "95%" }}
                                            name="total"
                                            id="total"
                                            value={parseFloat(displayValue(Number(data.total ?? 0), 'emissions').toFixed(2))}
                                            disabled={!isStateSelected}
                                            onChange={handleChange}
                                        />
                                    </td>
                                    <td className="cee_value" style={{ verticalAlign: "middle" }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
                                        <span>
                                            {displayEmissionsUnit(
                                            <>kg CO<sub>2</sub> eq/m<sup>3</sup></>,
                                            <>lb CO<sub>2</sub> eq/yd<sup>3</sup></>
                                            )}
                                        </span>
                                            <ButtonGroup vertical size="sm">
                                                <ToggleButton
                                                    id="emissions-toggle-m3"
                                                    type="checkbox"
                                                    variant={emissionsUnit === 'kgCO2eq_m3' ? 'primary' : 'outline-primary'}
                                                    checked={emissionsUnit === 'kgCO2eq_m3'}
                                                    onChange={toggleEmissionsUnit}
                                                    disabled={!isStateSelected}
                                                >
                                                    {unitSystem === 'metric' ? 'kg CO₂eq/m³' : 'lb CO₂eq/yd³'}
                                                </ToggleButton>

                                                <ToggleButton
                                                    id="emissions-toggle-ton"
                                                    type="checkbox"
                                                    variant={emissionsUnit === 'kgCO2eq_ton' ? 'primary' : 'outline-primary'}
                                                    checked={emissionsUnit === 'kgCO2eq_ton'}
                                                    onChange={toggleEmissionsUnit}
                                                    disabled={!isStateSelected}
                                                    style={{ whiteSpace: 'nowrap' }}
                                                >
                                                    kg CO₂eq/short ton
                                                </ToggleButton>
                                            </ButtonGroup>

                                        </div>
                                    </td>
                                </tr>
                                {/*<tr>*/}
                                {/*    <td>Total Cost</td>*/}
                                {/*    <td>*/}
                                {/*        <Form.Control*/}
                                {/*            type="number"*/}
                                {/*            size="sm"*/}
                                {/*            step="0.0001"*/}
                                {/*            style={{ width: "95%" }}*/}
                                {/*            name="total"*/}
                                {/*            id="total"*/}
                                {/*            value={parseFloat(displayValue(Number(data.total ?? 0), 'cost').toFixed(4))}*/}
                                {/*            onChange={handleChange}*/}
                                {/*        />*/}
                                {/*    </td>*/}
                                {/*    <td className="cee_value" style={{ textAlign: "right" }}>*/}
                                {/*        {displayUnit('$/m³', '$/yd³')}*/}
                                {/*    </td>*/}
                                {/*</tr>*/}
                            </table>
                        </div>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="pcb__box-footer-left">
                        <Button variant="secondary" onClick={goBack}>Cancel</Button>
                    </div>
                </Col>
                <Col>
                    <div className="pcb__box-footer-right">
                        <Button variant="primary" disabled={!isStateSelected} onClick={handleSubmit}>Save and Next</Button>
                    </div>
                </Col>
            </Row>

            <Modal show={errorPopupShow} onHide={handleErrorPopupClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>{error}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleErrorPopupClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={stateWarningShow} onHide={() => setStateWarningShow(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>State Required</Modal.Title>
                </Modal.Header>
                <Modal.Body>Please select a state before continuing.</Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setStateWarningShow(false)}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={validationErrorShow} onHide={() => setValidationErrorShow(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Validation Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>Please fix the highlighted errors before continuing. All fields are required and number values must be greater than or equal to 0.</Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setValidationErrorShow(false)}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
        </>
    );
};

export default AsphaltEmission;
